import { useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import { errorMessage } from '../lib/format';
import { useTable } from '../lib/useTable';
import type { ClassType } from '../lib/types';
import { PageHead } from '../components/AppLayout';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

const PALETTE = [
  '#7a4b2b',
  '#e08a3c',
  '#2e8b57',
  '#3b7dd8',
  '#9b59b6',
  '#c0392b',
  '#16a085',
  '#d4a017',
];

export function ClassTypesPage() {
  const { rows, loading, error, reload } = useTable<ClassType>('class_types', 'name');
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<ClassType | null>(null);
  const [creating, setCreating] = useState(false);

  async function remove(row: ClassType) {
    const ok = await confirm({
      title: `Delete class type "${row.name}"?`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    const { error } = await supabase.from('class_types').delete().eq('id', row.id);
    if (error) {
      toast.error(
        error.code === '23503'
          ? `"${row.name}" is used by scheduled classes — delete those classes first.`
          : errorMessage(error),
      );
    } else {
      toast.success(`Class type "${row.name}" deleted.`);
      void reload();
    }
  }

  return (
    <>
      <PageHead
        title="Class Types"
        subtitle="The kinds of classes you offer (Puppy Basics, Agility…). Reused when scheduling."
        action={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            + Add class type
          </button>
        }
      />

      <div className="card">
        {error && <p className="error-text">{error}</p>}
        {loading ? (
          <p className="muted">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="empty">
            <span className="empty-emoji">🏷️</span>
            No class types yet. Add one so you can schedule it later.
          </div>
        ) : (
          <table className="table cards">
            <thead>
              <tr>
                <th>Class type</th>
                <th>Default length</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <span className="row" style={{ gap: '0.5rem' }}>
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 4,
                          background: row.color,
                          display: 'inline-block',
                        }}
                      />
                      <span style={{ fontWeight: 600 }}>{row.name}</span>
                    </span>
                  </td>
                  <td className="muted" data-label="Length">
                    {row.default_duration_minutes ? `${row.default_duration_minutes} min` : '—'}
                  </td>
                  <td>
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(row)}>
                        Edit
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => void remove(row)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(creating || editing) && (
        <ClassTypeForm
          classType={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            void reload();
          }}
        />
      )}
    </>
  );
}

function ClassTypeForm({
  classType,
  onClose,
  onSaved,
}: {
  classType: ClassType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(classType?.name ?? '');
  const [color, setColor] = useState(classType?.color ?? PALETTE[0]);
  const [duration, setDuration] = useState(
    classType?.default_duration_minutes ? String(classType.default_duration_minutes) : '60',
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      name: name.trim(),
      color,
      default_duration_minutes: duration ? Number(duration) : null,
    };
    const query = classType
      ? supabase.from('class_types').update(payload).eq('id', classType.id)
      : supabase.from('class_types').insert(payload);
    const { error } = await query;
    setBusy(false);
    if (error) setErr(errorMessage(error));
    else onSaved();
  }

  return (
    <Modal title={classType ? 'Edit class type' : 'Add class type'} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="ct-name">Name</label>
          <input
            id="ct-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Puppy Basics"
            required
            autoFocus
          />
        </div>
        <div className="field">
          <label htmlFor="ct-duration">Default length (minutes)</label>
          <input
            id="ct-duration"
            type="number"
            min={5}
            step={5}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />
        </div>
        <div className="field">
          <label>Calendar color</label>
          <div className="row wrap" style={{ gap: '0.4rem' }}>
            {PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                aria-label={`Color ${c}`}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: c,
                  border: color === c ? '3px solid var(--fg)' : '2px solid var(--border)',
                  cursor: 'pointer',
                  padding: 0,
                }}
              />
            ))}
          </div>
        </div>
        {err && <p className="error-text">{err}</p>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
